/**
 * At-rest encryption for the roster cache (R10, N-F4-04): AES-256-GCM with a per-device key the
 * server hands out at registration. The key is imported NON-extractable and the CryptoKey object
 * itself is stored in IndexedDB (structured-clonable), so script running later can use it but
 * cannot read its bytes. Revoking the device wipes the key and every sealed row.
 *
 * This protects against casual extraction of a lost phone's storage; it is not a defence against
 * code running inside the app origin.
 */

export interface SealedBox {
  __sealed: 1
  alg: 'A256GCM'
  iv: Uint8Array
  ct: ArrayBuffer
}

export function isSealed(v: unknown): v is SealedBox {
  return typeof v === 'object' && v !== null && (v as { __sealed?: unknown }).__sealed === 1
}

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64.replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(new ArrayBuffer(bin.length))
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/** Import a raw 256-bit key delivered as base64/base64url. */
export async function importVaultKey(rawBase64: string): Promise<CryptoKey> {
  const raw = base64ToBytes(rawBase64)
  if (raw.byteLength !== 32) throw new Error('Vault key must be 32 bytes')
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

/** For dev/test: a random non-extractable key. */
export async function generateVaultKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'])
}

const enc = new TextEncoder()
const dec = new TextDecoder()

export async function seal(key: CryptoKey, value: unknown): Promise<SealedBox> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(JSON.stringify(value)),
  )
  return { __sealed: 1, alg: 'A256GCM', iv, ct }
}

export async function unseal<T>(key: CryptoKey, box: SealedBox): Promise<T> {
  const iv = new Uint8Array(box.iv)
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, box.ct)
  return JSON.parse(dec.decode(pt)) as T
}

/** Seal when a key is present, else store plain. */
export async function maybeSeal<T>(key: CryptoKey | null, value: T): Promise<T | SealedBox> {
  return key ? seal(key, value) : value
}

/** Open a row that may or may not be sealed. Throws if sealed and no key (wiped device). */
export async function maybeUnseal<T>(key: CryptoKey | null, data: T | SealedBox): Promise<T> {
  if (!isSealed(data)) return data
  if (!key) throw new Error('Roster is encrypted and this device has no key')
  return unseal<T>(key, data)
}
